import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, Shield, HelpCircle, FileText, ArrowRight } from 'lucide-react';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-saverly-primary to-saverly-secondary text-white py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Have questions? We're here to help! Browse our resources or reach out directly.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Contact Methods */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-saverly-dark mb-8 text-center">
            Contact Options
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* General Support */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow duration-200">
              <div className="w-14 h-14 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-full flex items-center justify-center mb-6">
                <MessageCircle className="h-7 w-7 text-saverly-primary" />
              </div>
              <h3 className="text-xl font-bold text-saverly-dark mb-3">
                General Support
              </h3>
              <p className="text-gray-600 mb-4">
                Questions about features, billing, or account management? Our support team is here to help.
              </p>
              <a 
                href="mailto:support@getsaverly.com"
                className="inline-flex items-center gap-2 text-saverly-primary font-semibold hover:text-saverly-secondary transition-colors duration-200"
              >
                <Mail className="h-4 w-4" />
                support@getsaverly.com
              </a>
            </div>

            {/* Privacy Concerns */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow duration-200">
              <div className="w-14 h-14 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-full flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-saverly-primary" />
              </div>
              <h3 className="text-xl font-bold text-saverly-dark mb-3">
                Privacy & Security
              </h3>
              <p className="text-gray-600 mb-4">
                Have privacy concerns or questions about how we handle your data? Contact our privacy team.
              </p>
              <a 
                href="mailto:privacy@getsaverly.com"
                className="inline-flex items-center gap-2 text-saverly-primary font-semibold hover:text-saverly-secondary transition-colors duration-200"
              >
                <Mail className="h-4 w-4" />
                privacy@getsaverly.com
              </a>
            </div>
          </div>
        </div>

        {/* Helpful Resources */}
        <div className="max-w-4xl mx-auto mb-24">
          <h2 className="text-3xl font-bold text-saverly-dark mb-12 text-center">
            Helpful Resources
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Features */}
            <Link 
              to="/features"
              className="group bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md hover:border-saverly-primary/20 transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HelpCircle className="h-6 w-6 text-saverly-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-saverly-dark mb-2 group-hover:text-saverly-primary transition-colors duration-200">
                    Features
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Learn about all the features Saverly offers to help you save money and eat better.
                  </p>
                  <span className="inline-flex items-center gap-2 text-saverly-primary font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                    Explore Features
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* How It Works */}
            <Link 
              to="/how-it-works"
              className="group bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md hover:border-saverly-primary/20 transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-saverly-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-saverly-dark mb-2 group-hover:text-saverly-primary transition-colors duration-200">
                    How It Works
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Step-by-step guide on how Saverly helps you plan meals and save money at the grocery store.
                  </p>
                  <span className="inline-flex items-center gap-2 text-saverly-primary font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                    Learn How
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Privacy Policy */}
            <Link 
              to="/privacy-policy"
              className="group bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md hover:border-saverly-primary/20 transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-saverly-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-saverly-dark mb-2 group-hover:text-saverly-primary transition-colors duration-200">
                    Privacy Policy
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Understand how we collect, use, and protect your personal information.
                  </p>
                  <span className="inline-flex items-center gap-2 text-saverly-primary font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                    Read Policy
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Terms of Use */}
            <Link 
              to="/terms-of-use"
              className="group bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md hover:border-saverly-primary/20 transition-all duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-saverly-primary/10 to-saverly-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="h-6 w-6 text-saverly-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-saverly-dark mb-2 group-hover:text-saverly-primary transition-colors duration-200">
                    Terms of Use
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Review the terms and conditions for using Saverly's services.
                  </p>
                  <span className="inline-flex items-center gap-2 text-saverly-primary font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                    View Terms
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-saverly-primary to-saverly-secondary rounded-2xl shadow-lg p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Start Saving?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Download Saverly today and start your journey to smarter meal planning and grocery shopping.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center gap-2 bg-white text-saverly-primary px-8 py-4 rounded-full font-bold hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Download Now
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;

